import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { capitalizeFirstLetter } from '@common-fe/common-fe';
import { snakeCase, toString } from 'lodash';

import { api } from '@/api';
import { OptionKey, PaginationParams } from '@/common/types';
import { API_URL, IClaimsResponseAggregatorDto } from '@/dtos';
import { getObservedOrganizationIdsFromURLSearchParamsAndToken } from '@/modules/core/hooks/useHistory';
import { PaymentType } from '@/modules/transaction/transaction.types';

import { ClaimDto, ClaimsStatus } from '../types/claims.constants';

export const QUERY_KEY = 'useClaimsList';

export interface ClaimDtoPayload {
  claim_id: number;
  employee_name: string;
  escalation_note?: string;
  category_type: string;
  payment_type: PaymentType;
  age: string;
  claim_status: string;
  is_appealed?: boolean;
  appeal_date?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  created_at: string;
  updated_at: string;
  organization_id?: string;
  organization_name?: string;
  employer_name?: string;
  subsidiary_name?: string;
  is_organization_locked?: boolean;
  claims_teams?: Array<{ id: number; name?: string }>;
}

interface ResponseData {
  content: ClaimDtoPayload[];
  number: number;
  numberOfElements: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const RESPONSE_KEY = 'claimsListPageResponse';
const PARAMS_KEY = 'claimsListPageRequestParams';
const EXTRA_KEY = 'extraKey';
const createExtraKey = (props: {
  isOnlyMyAssignments?: boolean;
  isShowOnlyEscalated?: boolean;
  isAppealed?: boolean;
  isAppealProcessed?: boolean;
}) =>
  `${props?.isOnlyMyAssignments ? '_isOnlyMyAssignments' : ''}${
    props?.isShowOnlyEscalated ? '_isShowOnlyEscalated' : ''
  }${props?.isAppealed === true ? '_isAppealed' : props?.isAppealed === false ? '_isNotAppealed' : ''}${
    props?.isAppealProcessed === true
      ? '_isAppealProcessed'
      : props?.isAppealProcessed === false
        ? '_isAppealNotProcessed'
        : ''
  }`;
export const setExtraKey = (props: {
  isOnlyMyAssignments?: boolean;
  isShowOnlyEscalated?: boolean;
  isAppealed?: boolean;
  isAppealProcessed?: boolean;
}) => sessionStorage.setItem(EXTRA_KEY, createExtraKey(props));
const getClaimsData = (key: string): ResponseData => {
  const storedExtraKey = sessionStorage.getItem(EXTRA_KEY) || '';
  const observedOrganizationPath = getObservedOrganizationIdsFromURLSearchParamsAndToken();
  const storageKey = `${key}${storedExtraKey}`;

  if (observedOrganizationPath) {
    return JSON.parse(
      JSON.parse(sessionStorage.getItem(storageKey) || 'null')?.[observedOrganizationPath] || 'null'
    );
  }

  return JSON.parse(sessionStorage.getItem(storageKey) || 'null');
};
const store = (params?: string, response?: string, extraKey?: string) => {
  const observedOrganizationPath = getObservedOrganizationIdsFromURLSearchParamsAndToken();
  if (response)
    sessionStorage.setItem(
      `${RESPONSE_KEY}${extraKey || ''}`,
      observedOrganizationPath ? JSON.stringify({ [observedOrganizationPath]: response }) : response
    );
  if (params)
    sessionStorage.setItem(
      `${PARAMS_KEY}${extraKey || ''}`,
      observedOrganizationPath ? JSON.stringify({ [observedOrganizationPath]: params }) : params
    );
};
export const removeStoredClaimsResponses = () => {
  sessionStorage.removeItem(RESPONSE_KEY);
  sessionStorage.removeItem(PARAMS_KEY);
  sessionStorage.removeItem(EXTRA_KEY);
};

export const getFollowingClaimId = (id?: string, isNext?: boolean) => {
  if (!id) {
    return '';
  }

  const responseData = getClaimsData(RESPONSE_KEY);

  if (!responseData?.content?.length) return '';

  const currentIndex = responseData?.content?.findIndex((claim) => claim.claim_id === parseFloat(id));

  if (currentIndex === -1) return '';

  if (!isNext) return toString(responseData?.content[currentIndex - 1]?.claim_id);

  const nextClaimId = toString(responseData?.content[currentIndex + 1]?.claim_id);

  if (nextClaimId) {
    return nextClaimId;
  }

  return '';
};

const getPageIndex = (isNext?: boolean) => {
  const responseData = getClaimsData(RESPONSE_KEY);

  if (!responseData) return '';

  const { totalPages, number } = responseData;

  if (!Number.isFinite(number) || !Number.isFinite(totalPages)) return '';

  if (isNext) {
    return number === totalPages - 1 ? '' : toString(number + 1);
  }

  return number === 0 ? '' : toString(number - 1);
};

export const isFollowingClaimAvailable = (id?: string, isNext?: boolean) => {
  if (!id) {
    return false;
  }

  const claimId = getFollowingClaimId(id, isNext);

  if (claimId) {
    return Boolean(claimId);
  }

  return Boolean(getPageIndex(isNext));
};

const formatData = (data: IClaimsResponseAggregatorDto[]) =>
  data.map(
    (claim): ClaimDto => ({
      id: `${claim.claim_id}`,
      employeeName: claim.employee_name || '',
      ...(claim?.escalation_note ? { escalationNote: claim?.escalation_note } : {}),
      categoryType: toString(claim.category_type),
      ...(claim?.payment_type
        ? {
            paymentType: claim?.payment_type as unknown as PaymentType,
          }
        : {}),
      age: claim.age as string,
      status: capitalizeFirstLetter(claim?.claim_status || '') as ClaimsStatus,
      ...(claim?.contact_first_name || claim?.contact_last_name
        ? {
            assignee: `${claim?.contact_first_name} ${claim?.contact_last_name}`.trim(),
          }
        : {}),
      createdAt: claim.created_at as unknown as string,
      updatedAt: claim.updated_at as unknown as string,
      ...(claim?.appeal_date ? { appealCreatedAt: claim.appeal_date as unknown as string } : {}),
      isAppealed: claim.is_appealed,
      organizationId: `${claim.organization_id}`,
      organizationName: claim.organization_name,
      employerName: claim.employer_name,
      subsidiaryName: claim?.subsidiary_name,
      isOrganizationLocked: claim.is_organization_locked,
      ...(claim?.claims_teams?.length
        ? {
            claimsTeams: claim.claims_teams.map((item) => ({ id: toString(item.id), name: item.name })),
          }
        : {}),
    })
  ) as ClaimDto[];

export interface ClaimsQueryParams extends Partial<PaginationParams> {
  hasAvatar?: boolean;
  paymentType?: PaymentType;
  dateRange?: Array<string | null>;
  statuses?: OptionKey[];
  enabled?: boolean;
  isNext?: boolean;
  isOnlyMyAssignments?: boolean;
  isShowOnlyEscalated?: boolean;
  isAppealed?: boolean;
  isAppealProcessed?: boolean;
}

const removeClaimIdFormat = (searchString: string) => {
  const isClaimId = searchString.length === 9 && searchString[1] === '-' && searchString[5] === '-';
  const claimId = Number(searchString.replace(/-/g, ''));
  if (isClaimId && !Number.isNaN(claimId)) return claimId.toString();
  return searchString;
};

const useClaimsListQuery = ({
  page,
  perPage,
  searchString,
  statuses,
  paymentType,
  dateRange = [],
  isNext,
  enabled = true,
  isOnlyMyAssignments,
  isShowOnlyEscalated,
  isAppealed,
  isAppealProcessed,
}: ClaimsQueryParams) => {
  const [periodFrom, periodTo] = dateRange;
  const storedParams = useMemo(() => {
    if (typeof isNext !== 'boolean') {
      return null;
    }

    const paramsData = getClaimsData(PARAMS_KEY);

    if (!paramsData) {
      return null;
    }

    const pageIndex = getPageIndex(isNext);

    if (!pageIndex) {
      return null;
    }

    return {
      ...paramsData,
      page: pageIndex,
    };
  }, [isNext]);
  const params = storedParams || {
    ...(searchString ? { search_value: removeClaimIdFormat(searchString) } : {}),
    page_size: perPage || 20,
    page,
    ...(statuses?.length
      ? { claim_status: statuses.map((status) => snakeCase(`${status}`).toUpperCase()).join(',') }
      : {}),
    ...(paymentType ? { payment_type: paymentType } : {}),
    ...(periodFrom ? { period_from: periodFrom } : {}),
    ...(periodTo ? { period_to: periodTo } : {}),
    ...(isOnlyMyAssignments ? { only_my_assignments: true } : {}),
    ...(isShowOnlyEscalated ? { show_only_escalated: true } : {}),
    ...(isAppealed !== undefined ? { is_appealed: isAppealed } : {}),
    ...(isAppealed ? { field: 'APPEAL_DATE', direct: 'ASC' } : {}),
    ...(isAppealProcessed !== undefined ? { is_appeal_processed: isAppealProcessed } : {}),
  };
  const { isLoading, isSuccess, isError, data, refetch, isRefetching } = useQuery(
    [
      QUERY_KEY,
      page,
      perPage,
      statuses,
      searchString,
      paymentType,
      isNext,
      enabled,
      periodFrom,
      periodTo,
      isOnlyMyAssignments,
      isShowOnlyEscalated,
      isAppealed,
      isAppealProcessed,
    ],
    () =>
      api.get(API_URL.CLAIMS_SEARCH, {
        params,
      }),
    {
      cacheTime: 0,
      enabled,
    }
  );

  if (enabled) {
    const extraKey = createExtraKey({ isOnlyMyAssignments, isShowOnlyEscalated, isAppealed, isAppealProcessed });
    store(JSON.stringify(params), JSON.stringify(data?.data), extraKey);
  }

  const total = useMemo<number>(
    // @ts-ignore
    () => (isLoading ? undefined : data?.data?.total_elements || data?.data?.totalElements),
    [data, isLoading]
  );

  return {
    formatedData: formatData(data?.data?.content || []),
    data: data?.data?.content || [],
    isError,
    total,
    isLoading,
    isSuccess,
    refetch,
    isRefetching,
  };
};

export default useClaimsListQuery;
